import React, { useState, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';

import Card from '@components/Card/Card';
import Select from '@controls/select';
import imgInfo from '@img/Information.png';
import { cloneDeep } from '@modules/_misc';
import { DEFUNCT_CAMPUS_CODES } from '@modules/campus';
import { date_reached, months_passed, formatDateTime } from '@modules/date';
import withRouter from '@wrappers/withRouter';

const FindYourTerm = props => {
	const terms = useSelector(state => state.global.terms),
		campuses = useSelector(state => state.global.campuses),
		[campusId, setCampusId] = useState(''),
		[appType, setAppType] = useState(''),
		termType = useMemo(() => (campusId === '108908' ? 'Flex Term' : 'Standard Term'), [campusId]),
		optCampuses = useMemo(() => getCampusOptions(campuses), [campuses]),
		termOptions = useMemo(() => findTerms('AVAILABLE'), [appType, campusId]),
		openingSoon = useMemo(() => findTerms('OPENING'), [appType, campusId]),
		closed = useMemo(() => findTerms('CLOSED'), [appType, campusId]);

	function findTerms(which) {
		let returnedTerms = [];

		//NO APP TYPE, RETURN NONE
		if (appType && campusId) {
			let international = appType.split('_')[1] === 'I',
				app_type_name = '';

			if (appType.includes('FM_')) {
				app_type_name = 'freshman';
			} else if (appType.includes('TR_')) {
				app_type_name = 'transfer';
			} else if (appType.includes('RE_') || appType.includes('RET_')) {
				app_type_name = 're_entry';
			}

			//Look through defaults and see if any campus has override
			terms.default_undergrad
				.filter(x => x.field_master_term_type === termType && !x.title.includes('Direct Admit'))
				.forEach(defaultTerm => {
					let unverified_term = '',
						term_overwritten = false,
						app_type_terms = defaultTerm[`field_${app_type_name}`];

					//Look through this term and find object related to international/domestic
					app_type_terms.forEach(obj => {
						if (
							(international && obj.field_type === 'International') ||
							(!international && obj.field_type === 'Domestic')
						) {
							unverified_term = cloneDeep(defaultTerm);
						}
					});

					if (unverified_term) {
						terms.campus_undergrad
							.filter(x => x.field_master_term_type === termType && x.field_program_specific !== '1')
							.forEach(campusTerm => {
								//Global term doesnt match
								if (unverified_term.field_global_term === campusTerm.field_global_term) {
									//Term DOESNT match campus
									if (campusTerm.field_campuses.includes(campusId)) {
										app_type_terms = campusTerm[`field_${app_type_name}_campus`];

										app_type_terms.forEach(obj => {
											if (
												(international && obj.field_type === 'International') ||
												(!international && obj.field_type === 'Domestic')
											) {
												unverified_term = cloneDeep(campusTerm);
												term_overwritten = true;
											}
										});
									}
								}
							});

						//Do logic on final term
						let return_term = '',
							final_app_type_terms = term_overwritten
								? unverified_term[`field_${app_type_name}_campus`]
								: unverified_term[`field_${app_type_name}`];

						final_app_type_terms.forEach(obj => {
							if (
								(international && obj.field_type === 'International') ||
								(!international && obj.field_type === 'Domestic')
							) {
								unverified_term.open_date = obj.field_opens;
								unverified_term.close_date = obj.field_closes;

								if (obj.field_opens === obj.field_closes && obj.field_closes === obj.field_pay) {
									unverified_term = '';
								}

								if (!date_reached(obj.field_opens)) {
									if (months_passed(obj.field_opens) > -3 && which === 'OPENING') {
										return_term = unverified_term;
									}
								} else if (date_reached(obj.field_closes)) {
									if (months_passed(obj.field_closes) < 3 && which === 'CLOSED') {
										return_term = unverified_term;
									}
								} else if (which === 'AVAILABLE') {
									return_term = unverified_term;
								}
							}
						});

						if (return_term) returnedTerms.push(return_term);
					}
				});
		}

		return returnedTerms;
	}

	function formatTerms(terms, which) {
		return terms.length
			? terms.map(term => {
					if (which === 'OPENING') {
						return (
							<p key={term.title} className='term-search-result'>
								{term.title} | Opening: {formatDateTime(term.open_date, true)}
							</p>
						);
					}

					if (which === 'CLOSED') {
						return (
							<p key={term.title} className='term-search-result'>
								{term.title} | Closed: {formatDateTime(term.close_date, true)}
							</p>
						);
					}

					return (
						<p key={term.title} className='term-search-result'>
							{term.title}
						</p>
					);
				})
			: [
					<p key='no-terms' className='term-search-result'>
						No terms found.
					</p>
				];
	}

	return (
		<Card
			title='FIND YOUR TERM'
			content={
				<div>
					<p className='intro-copy'>
						Here are when undergraduate degree applications are opening up. Use the dropdown to see when you
						can apply.
					</p>

					<p id='tooltip' className='intro-copy'>
						Looking for Graduate & Non-Degree term dates?
						<img className='informationTooltip' src={imgInfo} alt='Information' />
					</p>

					<Tooltip
						anchorId='tooltip'
						className='tooltipContainer'
						delayHide={1000}
						effect='solid'
						content={
							<>
								<p>
									Graduate and Non-Degree term dates vary by the different programs available at each
									campus.
								</p>
								<p>
									To find out when one of these applications open check in with the campus you are
									applying to.
								</p>
							</>
						}
					/>

					<Row>
						<Select
							value={campusId}
							label='Campus'
							placeholder='Select Campus'
							name='campusId'
							opts={optCampuses}
							onChange={val => setCampusId(val)}
							required={true}
							cols={6}
						/>

						<Select
							label='App Type'
							placeholder='Select App Type'
							name='appType'
							value={appType}
							opts={app_type_opts}
							onChange={val => setAppType(val)}
							required={true}
							cols={6}
						/>
					</Row>

					<h3>Open:</h3>
					{formatTerms(termOptions, '')}

					<h3>Coming soon:</h3>
					{formatTerms(openingSoon, 'OPENING')}

					<h3>Closed:</h3>
					{formatTerms(closed, 'CLOSED')}
				</div>
			}
		/>
	);
};

const getCampusOptions = campuses =>
	campuses
		.filter(
			(_c, i, self) =>
				!DEFUNCT_CAMPUS_CODES.includes(_c.field_abbreviation) &&
				self.findIndex(c => c.title.split(' ').join('') === _c.title.split(' ').join('')) === i // tms:  Flex appears twice (for PKS and MIL), but with different spacing in the title
		)
		.map(c => ({ val: c.id, label: c.title }));

export default withRouter(FindYourTerm);

const app_type_opts = [
	{ val: 'FM_D', label: 'First-Year (Domestic)' },
	{ val: 'TR_D', label: 'Transfer (Domestic)' },
	{ val: 'RE_D', label: 'Re-Entry (Domestic)' },
	{ val: 'FM_I', label: 'First-Year (International)' },
	{ val: 'TR_I', label: 'Transfer (International)' },
	{ val: 'RE_I', label: 'Re-Entry (International)' }
];
